import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Divider,
  Stack,
  Typography,
  ButtonBase,
  IconButton,
  Tooltip,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getNavItems, isNavItemActive, SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from './navConfig';
import { useSidebar } from './SidebarContext';
import { neonBlue, stormGrey } from '../../theme/colors';

const SideNav = () => {
  const location = useLocation();
  const { collapsed, toggleCollapsed } = useSidebar();
  const userRole = localStorage.getItem('userRole');
  const companyName = localStorage.getItem('companyName') || 'Elite Riders';
  const navItems = getNavItems(userRole);

  return (
    <Box
      sx={{
        width: { lg: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH },
        flexShrink: 0,
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column',
        borderRight: `1px solid ${stormGrey[200]}`,
        bgcolor: 'background.paper',
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.drawer,
        transition: (theme) =>
          theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        overflow: 'hidden',
      }}
    >
      <Stack
        spacing={2}
        sx={{
          p: collapsed ? 2 : 3,
          alignItems: collapsed ? 'center' : 'stretch',
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent={collapsed ? 'center' : 'flex-start'}
          sx={{ width: '100%' }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.contrastText',
              flexShrink: 0,
            }}
          >
            <LocalShippingIcon />
          </Box>
          {!collapsed && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {companyName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                Delivery Management
              </Typography>
            </Box>
          )}
        </Stack>
      </Stack>

      <Divider />

      <Box component="nav" sx={{ flex: 1, px: collapsed ? 1 : 2, py: 2, overflowY: 'auto' }}>
        <Stack component="ul" spacing={0.5} sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {navItems.map((item) => {
            const active = isNavItemActive(location.pathname, item.href);
            const Icon = item.icon;

            const navButton = (
              <ButtonBase
                component={RouterLink}
                to={item.href}
                sx={{
                  alignItems: 'center',
                  borderRadius: 1,
                  display: 'flex',
                  gap: collapsed ? 0 : 1.5,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  px: collapsed ? 1 : 1.5,
                    py: 1,
                    px: 1.25,
                    width: '100%',
                  minWidth: 0,
                  textAlign: 'left',
                  color: active ? neonBlue[600] : stormGrey[600],
                  bgcolor: active ? neonBlue[50] : 'transparent',
                  '&:hover': {
                    bgcolor: active ? neonBlue[50] : stormGrey[100],
                  },
                }}
              >
                <Icon fontSize="small" />
                {!collapsed && (
                  <Typography variant="body2" fontWeight={active ? 600 : 500} noWrap>
                    {item.title}
                  </Typography>
                )}
              </ButtonBase>
            );

            return (
              <Box component="li" key={item.key} sx={{ display: 'flex' }}>
                {collapsed ? (
                  <Tooltip title={item.title} placement="right" arrow>
                    {navButton}
                  </Tooltip>
                ) : (
                  navButton
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>

      <Divider />

      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'flex-end',
        }}
      >
        <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right" arrow>
          <IconButton
            onClick={toggleCollapsed}
            size="small"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            sx={{
              color: 'text.secondary',
              border: `1px solid ${stormGrey[200]}`,
            }}
          >
            {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default SideNav;
